import { useEffect, useRef, useState } from 'react';
import { AUDIO_MEDIA_FORMATS, inspectAudioMedia, type AudioMediaKind, type AudioMediaLicense } from '@djgg/core';
import { isValidAssetName, slugify, type Creation } from '@djgg/creations';
import { useServices } from '../services/context.js';
import { useAttributedAction } from '../hooks/useAttributedAction.js';

/**
 * Uses the ordinary asset upload/attachment path, not a separate sound store.
 * Mounted in exactly two places: the Studio composer's Upload source (after the
 * first build; `initialName` is the name chosen on the composer's What step)
 * and the wizard's pre-build draft staging (`draftStaging`, no name yet).
 */
export function AudioUploadPanel({ creation, kind, draftStaging, initialName, onUploaded }: {
  creation: Creation & { id: string };
  kind: AudioMediaKind;
  /** Pre-first-build staging from the wizard: different notice, nothing to bind yet. */
  draftStaging: boolean;
  /** The composer's chosen name; '' lets the picked file name it. */
  initialName: string;
  /** Called after a successful upload (the composer returns to the library). */
  onUploaded: () => void;
}) {
  return <AudioUploadForm key={`${creation.id}:${kind}:${initialName}`} creation={creation} kind={kind} draftStaging={draftStaging}
    initialName={initialName} onUploaded={onUploaded} />;
}
function AudioUploadForm({ creation, kind, draftStaging, initialName, onUploaded }: {
  creation: Creation & { id: string }; kind: AudioMediaKind; draftStaging: boolean; initialName: string; onUploaded: () => void;
}) {
  const services = useServices();
  const { runAttributed, attributionModal } = useAttributedAction();
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState(initialName);
  const [licenseKind, setLicenseKind] = useState<AudioMediaLicense['kind']>('creator-owned');
  const [source, setSource] = useState('');
  const [terms, setTerms] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [customLoop, setCustomLoop] = useState(false);
  const [loopStart, setLoopStart] = useState('0');
  const [loopEnd, setLoopEnd] = useState('');
  const alive = useRef(true), inFlight = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const replacing = creation.assets?.find(asset => asset.name === name);
  const format = AUDIO_MEDIA_FORMATS[kind];
  async function upload() {
    if (!file || inFlight.current || !isValidAssetName(name) || (replacing && !confirmed)) return;
    inFlight.current = true; setBusy(true); setError(''); setNotice('');
    try {
      if (file.size > format.maxBytes) throw new Error(`File is too large. Maximum ${Math.round(format.maxBytes / 1024)} KiB.`);
      const bytes = new Uint8Array(await file.arrayBuffer());
      const secondsToMs = (value: string) => {
        if (!/^\d+(?:\.\d{1,3})?$/.test(value.trim())) throw new Error('Enter loop times in seconds, with up to three decimal places.');
        return Math.round(Number(value) * 1000);
      };
      const loop = kind === 'music' && customLoop
        ? { loopStartMs: secondsToMs(loopStart), loopEndMs: secondsToMs(loopEnd) } : undefined;
      const metadata = inspectAudioMedia(bytes, kind, { kind: licenseKind, source, terms }, loop);
      let binary = '';
      for (let offset = 0; offset < bytes.length; offset += 8192) binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
      const dataBase64 = btoa(binary);
      if (!alive.current) return;
      await runAttributed(creation, async attributeTo => {
        if (!alive.current) return;
        try {
          await services.callable.uploadAssets({ creationId: creation.id,
            assets: [{ name, kind, dataBase64, license: metadata.license, ...(loop ? { loop } : {}) }], ...(attributeTo ? { attributeTo } : {}) });
          if (alive.current) {
            setFile(null); setConfirmed(false);
            if (fileInput.current) fileInput.current.value = '';
            setNotice(draftStaging
              ? 'Uploaded to this saved draft. Uploading does not bind or play the sound. After the first successful build, choose a binding in Assets Studio and publish saved media.'
              : 'Uploaded to the working copy. Choose a sound binding, then publish saved media to use it in a new game version. Existing versions are unchanged.');
            onUploaded();
          }
        } catch (err) { if (alive.current) setError(err instanceof Error ? err.message : 'Audio upload failed.'); }
      });
    } catch (err) { if (alive.current) setError(err instanceof Error ? err.message : 'Audio upload failed.'); }
    finally { inFlight.current = false; if (alive.current) setBusy(false); }
  }
  return <section className="panel audio-upload" aria-label={`Upload ${kind === 'music' ? 'music' : 'sound effect'}`}>
    <h3>Upload your {kind === 'music' ? 'music' : 'sound effect'}</h3>
    <p>Choose a WAV recording up to {format.maxDurationMs / 1000} seconds long. Give it a short name and tell us where it came from.</p>
    <details><summary>File requirements</summary><p>PCM WAV: 16-bit, mono or stereo, 8–48 kHz, maximum {Math.round(format.maxBytes / 1024)} KiB. Keep peaks below 95%. Convert other formats in your audio editor first.</p></details>
    <p>No AI generation or model charge. Uploading does not change the live game.</p>
    <label>WAV file <input ref={fileInput} className="input" type="file" accept="audio/wav,.wav" disabled={busy} onChange={event => {
      const picked = event.target.files?.[0] ?? null;
      setFile(picked);
      // A name the creator already chose (the composer's What step) wins; the
      // file name only fills an empty field.
      setName(current => current || slugify(picked?.name.replace(/\.wav$/i, '') ?? '').slice(0, 40).replace(/-+$/, ''));
      setConfirmed(false); setCustomLoop(false); setLoopStart('0'); setLoopEnd(''); setNotice(''); setError('');
    }} /></label>
    {file && file.size <= format.maxBytes && <LocalAudioPreview file={file} />}
    {kind === 'music' && <fieldset disabled={busy}>
      <legend>Music repeat range (optional)</legend>
      <p>The game repeats the whole recording by default. Set a range to play an intro once, then repeat only the selected part. Times are measured from the beginning of the file.</p>
      <label style={{ display: 'block', marginBottom: 8 }}><input type="checkbox" checked={customLoop} onChange={event => setCustomLoop(event.target.checked)} /> Choose custom loop points</label>
      {customLoop && <>
        <label style={{ display: 'block', marginBottom: 8 }}>Loop start (seconds) <input className="input" type="text" inputMode="decimal" value={loopStart} onChange={event => setLoopStart(event.target.value)} placeholder="2.5" /></label>
        <label style={{ display: 'block', marginBottom: 8 }}>Loop end (seconds) <input className="input" type="text" inputMode="decimal" value={loopEnd} onChange={event => setLoopEnd(event.target.value)} placeholder="30" /></label>
        <p className="muted">End must be after start and within the recording. The preview above plays the original file; the loop is used after binding and publishing.</p>
      </>}
    </fieldset>}
    <label>Sound name <input className="input" value={name} disabled={busy} maxLength={40} placeholder="engine-hum" onChange={event => { setName(event.target.value); setConfirmed(false); }} /></label>
    <p className="muted">A short name using lowercase letters, numbers and hyphens. For example: goal-cheer or calm-theme.</p>
    <label>Usage rights <select className="input" value={licenseKind} disabled={busy} onChange={event => setLicenseKind(event.target.value as AudioMediaLicense['kind'])}>
      <option value="creator-owned">I made and own this recording</option><option value="licensed">I have permission to use this recording</option>
    </select></label>
    <label>Recording source <input className="input" value={source} disabled={busy} maxLength={512} placeholder="Recorded by me, or the library and track name" onChange={event => setSource(event.target.value)} /></label>
    <label>Permission or license terms <textarea className="input" rows={3} value={terms} disabled={busy} maxLength={4096} placeholder="For example: My original recording, or CC BY 4.0 — credit Jane Doe" onChange={event => setTerms(event.target.value)} /></label>
    {replacing && <label><input type="checkbox" checked={confirmed} disabled={busy} onChange={event => setConfirmed(event.target.checked)} /> Replace {name} in the working copy. Published versions retain their original file.</label>}
    {error && <p role="alert">{error}</p>}{notice && <p role="status">{notice}</p>}
    <button type="button" className="btn" disabled={busy || !file || !isValidAssetName(name) || !source.trim() || !terms.trim() || (!!replacing && !confirmed)} onClick={() => void upload()}>{busy ? 'Uploading…' : 'Upload to working copy'}</button>
    {attributionModal}
  </section>;
}

/** Blob URLs stay on this device; selecting or previewing never uploads. */
function LocalAudioPreview({ file }: { file: File }) {
  const audio = useRef<HTMLAudioElement>(null);
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const next = URL.createObjectURL(file);
    const element = audio.current;
    setUrl(next); setFailed(false);
    const pauseWhenHidden = () => { if (document.hidden) element?.pause(); };
    document.addEventListener('visibilitychange', pauseWhenHidden);
    return () => {
      document.removeEventListener('visibilitychange', pauseWhenHidden);
      element?.pause();
      element?.removeAttribute('src');
      URL.revokeObjectURL(next);
    };
  }, [file]);
  return <div>
    <p>Listen before uploading. This preview stays on your device; it does not change the game.</p>
    <audio ref={audio} controls preload="none" src={url || undefined}
      aria-label={`Preview selected recording: ${file.name}`} style={{ width: '100%', maxWidth: 440 }}
      onError={() => setFailed(true)} />
    {failed && <p role="status">Your browser could not preview this recording. Upload validation still checks the WAV format and sound limits.</p>}
  </div>;
}
